import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { CampaignHud } from './components/CampaignHud'
import { CampaignStudioPrompt } from './components/CampaignStudioPrompt'
import { CreditsPage } from './components/CreditsPage'
import {
  DetailPanel,
  type PanelMode,
  type PanelStoryEpisode,
} from './components/DetailPanel'
import { SiteFooter } from './components/SiteFooter'
import { SocialContactRail } from './components/SocialContactRail'
import { InteractionRail } from './components/StageChrome'
import {
  VehicleViewToggle,
  type VehicleView,
} from './components/VehicleViewToggle'
import { STUDIO_HASH, pageForHash, titleForPage, type SitePage } from './routes'
import {
  selectAcceptedPlacementsForRenderer,
  type AcceptedPlacementRecord,
  type Placement,
} from './domain/placement'
import { storyEpisodes } from './view-model/campaignView'

const VehicleStage = lazy(() =>
  import('./components/VehicleStage').then((module) => ({
    default: module.VehicleStage,
  })),
)

const PlacementStudioPage = lazy(() =>
  import('./components/PlacementStudioPage').then((module) => ({
    default: module.PlacementStudioPage,
  })),
)

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  )

  useEffect(() => {
    const media = window.matchMedia(query)
    const update = () => setMatches(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [query])

  return matches
}

function buildDemoPlacementLayers(placements: readonly Placement[]) {
  const anchoredPlacements = placements.filter(
    (placement) => placement.surfaceAnchor,
  )
  return anchoredPlacements.length === 0
    ? []
    : [
        {
          id: 'campaign-demo-free-surface',
          panel: 'driver-front-door' as const,
          freeform: true,
          interactive: false,
          placements: anchoredPlacements,
        },
      ]
}

export default function App() {
  const [page, setPage] = useState<SitePage>(() => pageForHash(location.hash))
  const [panelMode, setPanelMode] = useState<PanelMode>('closed')
  const [storyIndex, setStoryIndex] = useState(0)
  const [vehicleView, setVehicleView] = useState<VehicleView>('open')
  const [demoReviewRecords, setDemoReviewRecords] = useState<
    readonly AcceptedPlacementRecord[]
  >([])
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const demoArtworkUrlsRef = useRef(new Set<string>())
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const mobilePanel = useMediaQuery('(max-width: 767px)')

  function closePanel({ restoreFocus = true } = {}) {
    setPanelMode('closed')
    if (restoreFocus) {
      window.requestAnimationFrame(() => returnFocusRef.current?.focus())
    }
  }

  function handleBack() {
    closePanel()
  }

  function handleStartStory(source: HTMLElement) {
    returnFocusRef.current = source
    setStoryIndex(0)
    setPanelMode('story')
  }

  function handlePublishDemoPlacement(placement: Placement) {
    if (!placement.surfaceAnchor) return
    const reviewedAt = new Date().toISOString()
    const record: AcceptedPlacementRecord = {
      recordId: `local-demo-${placement.id}`,
      reviewStatus: 'accepted',
      placement: { ...placement },
      submittedAt: reviewedAt,
      reviewedAt,
      reviewReference: `local-demo-only:${placement.id}`,
    }
    const displacedRecord = demoReviewRecords.find(
      (item) => item.placement.id === placement.id,
    )
    if (
      displacedRecord?.placement.kind === 'image' &&
      displacedRecord.placement.content !== placement.content &&
      demoArtworkUrlsRef.current.delete(displacedRecord.placement.content)
    ) {
      URL.revokeObjectURL(displacedRecord.placement.content)
    }
    if (placement.kind === 'image' && placement.content.startsWith('blob:')) {
      demoArtworkUrlsRef.current.add(placement.content)
    }
    setDemoReviewRecords((current) => [
      ...current.filter((item) => item.placement.id !== placement.id),
      record,
    ])
    setVehicleView('closed')
  }

  function clearDemoPlacements() {
    demoArtworkUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    demoArtworkUrlsRef.current.clear()
    setDemoReviewRecords([])
  }

  useEffect(() => {
    const initialPage = pageForHash(window.location.hash)
    document.title = titleForPage(initialPage)

    function syncRoute() {
      const nextPage = pageForHash(window.location.hash)
      setPage(nextPage)
      setPanelMode('closed')
      document.title = titleForPage(nextPage)
      window.scrollTo({ top: 0, behavior: 'instant' })
      if (nextPage === 'campaign') {
        window.requestAnimationFrame(() =>
          document.querySelector<HTMLElement>('.campaign-title')?.focus(),
        )
      }
    }

    window.addEventListener('hashchange', syncRoute)
    return () => window.removeEventListener('hashchange', syncRoute)
  }, [])

  useEffect(() => {
    const demoArtworkUrls = demoArtworkUrlsRef.current
    return () => {
      demoArtworkUrls.forEach((url) => URL.revokeObjectURL(url))
      demoArtworkUrls.clear()
    }
  }, [])

  useEffect(() => {
    if (!mobilePanel || panelMode === 'closed') return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [mobilePanel, panelMode])

  return (
    <>
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      {page === 'credits' ? (
        <CreditsPage />
      ) : page === 'studio' ? (
        <Suspense
          fallback={
            <main className="placement-studio" id="main-content">
              <div className="placement-studio__route-loading" role="status">
                Preparing the Placement Studio
              </div>
            </main>
          }
        >
          <PlacementStudioPage
            onPublishDemoPlacement={handlePublishDemoPlacement}
          />
        </Suspense>
      ) : (
        <main id="main-content">
          <section
            className="stage-shell"
            aria-label="Million Dollar Model X campaign stage"
          >
            <Suspense
              fallback={
                <div
                  className="vehicle-stage vehicle-stage--loading"
                  role="status"
                >
                  Preparing the interactive vehicle
                </div>
              }
            >
              <VehicleStage
                reducedMotion={reducedMotion}
                vehicleView={vehicleView}
                placementLayers={
                  vehicleView === 'closed'
                    ? buildDemoPlacementLayers(
                        selectAcceptedPlacementsForRenderer(demoReviewRecords),
                      )
                    : []
                }
              />
            </Suspense>

            <CampaignHud
              onStartStory={() => {
                const source =
                  document.querySelector<HTMLElement>('.story-button')
                if (source) handleStartStory(source)
              }}
            />
            <CampaignStudioPrompt />
            <SocialContactRail />
            <VehicleViewToggle value={vehicleView} onChange={setVehicleView} />
            <InteractionRail />

            {demoReviewRecords.length > 0 ? (
              <div className="campaign-demo-status" role="status">
                <span>
                  Local demo · {demoReviewRecords.length}{' '}
                  {demoReviewRecords.length === 1 ? 'placement' : 'placements'}
                  {vehicleView === 'open' ? ' · closed view only' : ''}
                </span>
                {vehicleView === 'open' ? (
                  <button
                    type="button"
                    onClick={() => setVehicleView('closed')}
                  >
                    View placement
                  </button>
                ) : null}
                <button type="button" onClick={clearDemoPlacements}>
                  Clear demo
                </button>
              </div>
            ) : null}

            {mobilePanel && panelMode !== 'closed' ? (
              <button
                className="panel-scrim"
                type="button"
                aria-label="Dismiss panel"
                onClick={() => closePanel()}
              />
            ) : null}

            <DetailPanel
              mode={panelMode}
              zone={null}
              episodes={storyEpisodes as PanelStoryEpisode[]}
              storyIndex={storyIndex}
              modal={mobilePanel}
              onRequestClose={() => closePanel()}
              onRequestBack={handleBack}
              onRequestInquiry={() => undefined}
              onInquiryReady={() => undefined}
              onStoryIndexChange={setStoryIndex}
            />
          </section>

          <section
            className="project-status"
            aria-labelledby="project-status-title"
          >
            <p className="section-eyebrow">Project status</p>
            <h2 id="project-status-title">
              Placement inventory is not yet published
            </h2>
            <p>
              The interactive vehicle is a campaign visualization. Real spots,
              prices, and panel coordinates will appear only after they are
              measured and mapped to the physical vehicle.
            </p>
            <a className="project-status__studio-link" href={STUDIO_HASH}>
              Create a branding mockup
              <span>Preview only · no reservation or payment</span>
            </a>
          </section>
        </main>
      )}
      <SiteFooter currentPage={page} />
    </>
  )
}
